import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
    DialogActionTrigger,
    DialogCloseTrigger,
  } from "@/components/ui/dialog"
  import {
    Button,
    Text,
    ButtonGroup,
    VStack,
    Spinner,
    Center,
    Input,
  } from "@chakra-ui/react"
  import { PasswordInput } from "@/components/ui/password-input"
  import { Controller, useForm, type SubmitHandler } from "react-hook-form"
  import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
  import { ToolsService, type ToolDetailPublic } from "@/client"
  import { Field } from "@/components/ui/field"
  import { useEffect, useState } from "react"
  import useCustomToast from "@/hooks/useCustomToast"
  import { handleError } from "@/utils"
  
  interface ToolDetailProps {
    isOpen: boolean
    onClose: () => void
    toolId: string | null
  }
  
  const ToolDetail = ({ isOpen, onClose, toolId }: ToolDetailProps) => {
    const queryClient = useQueryClient()
    const { showSuccessToast } = useCustomToast()
    const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({})
  
    const toggleVisibility = (name: string) => {
      setVisibleMap((prev) => ({ ...prev, [name]: !prev[name] }))
    }
  
    const {
      data: tool,
      isLoading: isToolLoading,
    } = useQuery<ToolDetailPublic>({
      queryKey: ["tool", toolId],
      queryFn: () => ToolsService.getTool({ toolId: toolId! }),
      enabled: !!toolId,
    })
  
    const {
      data: userVarValues,
      isLoading: isUserVarsLoading,
    } = useQuery<Record<string, string>>({
      queryKey: ["tool_user_vars", toolId],
      queryFn: () => ToolsService.getUserToolVariables({ toolId: toolId! }),
      enabled: !!toolId,
    })
  
    const mutation = useMutation({
      mutationFn: (values: Record<string, string>) =>
        ToolsService.setUserToolVariables({ toolId: toolId!, requestBody: { values } }),
      onSuccess: () => {
        showSuccessToast("도구 설정이 저장되었습니다.")
        queryClient.invalidateQueries({ queryKey: ["tool_user_vars", toolId] })
        onClose()
      },
      onError: handleError,
    })
  
    const {
      register,
      handleSubmit,
      control,
      reset,
      formState: { errors, isValid, isSubmitting },
    } = useForm<Record<string, string>>({ mode: "onBlur" })
  
    useEffect(() => {
      if (tool && userVarValues) {
        const initialValues: Record<string, string> = {}
        tool.user_variables?.forEach((v) => {
          initialValues[v.name] = userVarValues[v.name] ?? v.default ?? ""
        })
        reset(initialValues)
      }
    }, [tool, userVarValues, reset])
  
    const onSubmit: SubmitHandler<Record<string, string>> = (data) => {
      mutation.mutate(data)
    }
  
    const isLoading = isToolLoading || isUserVarsLoading
  
    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={({ open }) => !open && onClose()}
        size={{ base: "xs", md: "md" }}
        placement="center"
      >
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{tool?.name || "도구 정보"}</DialogTitle>
            </DialogHeader>
  
            <DialogBody>
              {isLoading || !tool ? (
                <Center py={4}>
                  <Spinner />
                </Center>
              ) : (
                <VStack align="start" gap={4}>
                  <Text>{tool.description}</Text>
  
                  <VStack align="start" gap={1} w="full">
                    <Text fontWeight="bold">사용자 변수:</Text>
  
                    {tool.user_variables?.map((v) => (
                        <Field
                            key={v.name}
                            label={`${v.variable_name} (${v.name})`}
                            required={v.required}
                            invalid={!!errors[v.name]}
                            errorText={errors[v.name]?.message}
                        >
                            {/* {v.description && (
                                <Text fontSize="sm" color="gray.500">
                                    {v.description}
                                </Text>
                            )} */}
                            {v.is_secret ? (
                            <Controller
                                name={v.name}
                                control={control}
                                rules={{
                                required: v.required ? `${v.name} 값은 필수입니다.` : false,
                                }}
                                render={({ field }) => (
                                <PasswordInput
                                    visible={visibleMap[v.name] ?? false}
                                    onVisibleChange={() => toggleVisibility(v.name)}
                                    defaultValue={field.value ?? ""}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    placeholder={v.placeholder || ""}
                                    type={v.name}
                                    errors={errors}
                                />
                                )}
                            />
                            ) : (
                            <>
                                <Input
                                id={v.name}
                                type="text"
                                placeholder={v.placeholder || ""}
                                {...register(v.name, {
                                    required: v.required ? `${v.name} 값은 필수입니다.` : false,
                                })}
                                />
                            </>
                            )}
                        </Field>
                        ))}
                  </VStack>
                </VStack>
              )}
            </DialogBody>
  
            <DialogFooter gap={2}>
              <ButtonGroup>
                <DialogActionTrigger asChild>
                  <Button variant="subtle" colorPalette="gray" disabled={isSubmitting}>
                    취소
                  </Button>
                </DialogActionTrigger>
                <Button
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  저장
                </Button>
              </ButtonGroup>
            </DialogFooter>
          </form>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    )
  }
  
  export default ToolDetail
  